<template>
    <div class="row">
        <div class="col-12 mb-4 d-flex flex-row flex-wrap align-items-center">
            <h1 class="mb-0">{{ $t('generate-events') }}</h1>

            <button 
                type="button"
                class="btn btn-outline-primary ml-auto mr-2" 
                @click="openMenu"
                :disabled="!groups.length || !from || !until"
            >
                {{  $t('add-events') }}
            </button>
            <button
                type="button"
                class="btn btn-outline-dark"
                @click="showModal = true"
                :disabled="!groups.length || !from || !until"
            >
                {{ $t('generate') }}
            </button>
        </div>

        <div class="col-12 mb-4">
            <div class="list-group border-radius-2 no-border">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('new-plan') }}
                </div>
                <div class="list-group-item d-flex flex-row flex-wrap align-items-center" style="gap: 8px">
                    <DateInput 
                        :value="from" 
                        @input="snapFrom"
                        :label="$t('from')"
                    />
                    <DateInput
                        :value="until"
                        @input="snapUntil"
                        :label="$t('to')"
                    />
                    <SelectInput
                        :value="typeSelected"
                        :label="$t('group-type')"
                        :options="groupTypes"
                        :disabled="!from || !until"
                        optionLabel="title"
                        @input="selectType"
                    />
                    <SelectInput 
                        :value="selectedSet"
                        @input="selectSet"
                        :label="$t('select-a-set')"
                        :options="recurEventSets"
                        :disabled="!typeSelected || menuIsSubmitting"
                        optionLabel="title"
                        :nullable="true"
                        :nullLabel="$t('new-set')"
                    />
                    <template v-if="from && until && typeSelected && selectedSet === null">
                        <TextInput :label="$t('title')" v-model="newTitle" />
                        <button 
                            v-if="selectedSet === null"
                            type="button"
                            class="btn btn-primary mt-3"
                            @click="createSet"
                            :disabled="!newTitle.length"
                        >
                            {{ $t('create') }}
                        </button>
                    </template>
                </div>
            </div>
        </div>
        <div class="col-12">
            <div v-if="!typeSelected && !selectedSet" class="list-group border-radius-2 no-border">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('resume-progress') }}
                </div>
                <div 
                    v-for="set in recurEventSets.filter(s => s.datefrom && s.dateto && s.grouptype_id)" 
                    :key="set.id"
                    class="list-group-item d-flex flex-column flex-md-row"
                    style="gap: 1rem; cursor: pointer;"
                    @click="loadSet(set)"
                >
                    <span>{{ set.title }}</span>
                    <span>{{ set.datefrom.split('-').reverse().join('/') }} - {{ set.dateto.split('-').reverse().join('/') }}</span>
                    <span></span>
                </div>
            </div>
        </div>

        <template v-if="from && until && typeSelected">
            <AdminTimetable
                ref="timetable"
                :from="from"
                :until="until"
                :events="events"
                :groups="groups"
                :students="students"
                :programme="programme"
                :disableSession="true"
                :year="year"
                @selectEvent="selectRecur"
                :yearColumnEventCb="() => {}"
                :groupColumnEventCb="() => {}"
                class="col-12"
            />

            <RecurEventMenu 
                ref="menu"
                :show="showMenu"
                @close="showMenu = false"
                :from="from"
                :until="until"
                :programme="programme"
                :groups="groups"
                :grouptypeId="typeSelected.id"
                :recurEventSet="selectedSet"
                @applyRecur="applyRecur"
                @newSet="addSet"
                @setDeleted="removeSet"
            />
        </template>

        <div v-else-if="alreadyApplied.length" class="col-12 mt-4">
            <div class="list-group border-radius-2 no-border">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('already-applied') }}
                </div>
                <div v-for="applied in alreadyApplied" :key="`${applied.tttimetable_id}-${applied.ttrecureventset_id}`" class="list-group-item d-flex align-items-center">
                    {{ applied.ttrecureventset_title }} - {{ applied.tttimetable_name }}
                    <button type="button" class="btn btn-sm btn-secondary ml-auto" @click="undoApply(applied)">{{ $t('undo') }}</button>
                </div>
            </div>
        </div>

        <b-modal v-model="showModal" ref="modal" :title="$t('generate-events')">
            <SelectInput 
                v-model="selectedTimetable"
                :label="$t('timetable')"
                :options="timetables"
                optionLabel="name"
                @input="value => snapApplyFrom(value.datefrom)"
            />
            <DateInput 
                :value="applyFrom" 
                @input="snapApplyFrom" 
                :label="$t('from')" 
                :disabled="!selectedTimetable"
                :min="selectedTimetable ? selectedTimetable.datefrom : ''"
                :max="selectedTimetable ? selectedTimetable.dateto : ''"
            />
            <template v-slot:modal-footer>
                <div class="d-flex">
                    <button 
                        type="button" 
                        class="btn btn-primary mr-2" 
                        @click="applySetToTT"
                        :disabled="!selectedTimetable"
                    >
                        {{ $t('generate') }}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" @click="showModal = false">{{ $t('cancel') }}</button>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import DateInput from 'src/components/Inputs/DateInput.vue'
import SelectInput from 'src/components/Inputs/SelectInput.vue'
import AdminTimetable from 'src/components/Timetables/AdminTimetable.vue'
import RecurEventMenu from "src/components/Timetables/Generator/RecurEventMenu.vue"
import { dateFromISODate, dateToISODate } from "src/modules/timetable/util"

export default {
    components: {
        DateInput,
        SelectInput,
        AdminTimetable,
        RecurEventMenu,
    },
    mounted() {
        Promise.all([
            this.fetchRecurEventSets(), 
            this.fetchGroupTypes(), 
            this.fetchTimetables(),
            this.fetchAlreadyApplied(),
        ]).then(() => {
            this.loading = false
        })
    },
    data() {
        return {
            loading: true,
            submitting: false,
            from: '',
            until: '',
            typeSelected: null,
            programme: null,
            year: null,
            events: [],
            groupTypes: [],
            groups: [],
            recurEventSets: [],
            selectedSet: null,
            students: [],
            studentsById: {},
            showMenu: false,
            showModal: false,
            timetables: [],
            selectedTimetable: null,
            applyFrom: '',
            newTitle: '',
            alreadyApplied: [],
        }
    },
    methods: {
        fetchRecurEventSets() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/all`, {
                method: 'GET',
                data: {},
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.recurEventSets = res.results
            }).catch(err => {
                this.$log.error.call(this, err)
            })
        },
        fetchTimetables() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/all`, {
                method: 'GET',
                data: { status: 1, ends_after: dateToISODate(new Date) }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.timetables = res.results
            }).catch(err => {
                this.$log.error.call(this, err)
            })
        },
        fetchGroupTypes() {
            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/grouptypes/all`, {
                method: 'POST',
                data: {
                    published_only: true,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.groupTypes = res.results
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-group-types'),
                    confirmCallBack: () => {
                        this.$router.replace('/timetables')
                    }
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
        fetchAlreadyApplied() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/applied`, {
                method: 'GET',
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.alreadyApplied = res.results
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.submitting = false
            })
        },
        createSet() {
            if (this.submitting) return Promise.resolve()
            this.submitting = true
            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/save`, {
                method: 'POST',
                data: {
                    title: this.newTitle,
                    datefrom: this.from,
                    dateto: this.until,
                    grouptype_id: this.typeSelected.id,
                    newTitle: this.newTitle,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.recurEventSets.push(res.result)
                this.selectSet(res.result)
            }).catch(err => {
                this.$log.error.call(this, err)
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
        selectType(type) {
            if (this.submitting) return

            this.typeSelected = type

            if (type.groups) {
                this.students = []
                this.groups = type.groups
                this.programme = this.$config.getValue('programmes')[type.programme_id]
                return
            }

            this.submitting = true
            this.$store.commit('showBlockingSpinner')
            
            this.programme = this.$config.getValue('programmes')[type.programme_id]

            this.fetchGroups().finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
        selectSet(set) {
            this.selectedSet = set

            if (this.selectedSet === null) {
                this.applyRecur(-1, []);
            }
            else {
                this.showMenu = true
            }
        },
        fetchGroups() {
            if (!this.typeSelected) return Promise.resolve()

            return this.$cfetch(`${this.$settings.APIBaseURL}/grouptypes/yeargroups/all`, {
                method: 'GET',
                data: { grouptype_id: this.typeSelected.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching group type')

                this.groups = res.results

                this.groups.forEach(gr => {
                    gr.students = gr.students.map(st => {
                        let user = {
                            ...st,
                            ...st.user,
                            group_id: gr.id,
                            year: gr.year,
                        }
                        delete user.user
                        return user
                    })
                })

                this.students = this.groups.reduce((res, cur) => { 
                    res.push(...cur.students)
                    return res
                }, [])
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-groups'),
                })
            })
        },
        async applySetToTT() {
            if (this.submitting) return
            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            const res = await this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/applied`, {
                method: 'GET',
                data: {
                    ttrecureventset_id: this.selectedSet.id,
                    tttimetable_id: this.selectedTimetable.id,
                },
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.submitting = false
            })

            if (res.status !== 'SUCCESS') {
                this.$log.error.call(this, res)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                })
            }
            
            let appliedRes, appliedRej
            const promise = new Promise(resolve => appliedRes = resolve, reject => appliedRej = reject)

            if (res.results.length > 0) {
                this.$store.commit('openDialog', {
                    title: this.$t('already-applied'),
                    content: this.$t('set-already-applied-msg'),
                    cancelBtn: true,
                    confirmCallBack: appliedRes,
                    cancelCallBack: appliedRej,
                    confirmBtnText: this.$t('yes'),
                    cancelBtnText: this.$t('no')
                })
            }

            await promise.catch()

            this.$store.commit('showBlockingSpinner')
            this.submitting = true

            this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/apply`, {
                method: 'POST',
                data: {
                    ttrecureventset_id: this.selectedSet.id,
                    tttimetable_id: this.selectedTimetable.id,
                    from: this.applyFrom,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error(res)

                this.$router.push(`/timetables/edit/${this.selectedTimetable.id}`)
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-generating-timetable'),
                })
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
                this.showModal = false
            })
        },
        async undoApply(alreadyApplied) {
            const { 
                tttimetable_name, 
                tttimetable_id, 
                ttrecureventset_title, 
                ttrecureventset_id,
            } = alreadyApplied

            let undoRes, undoRej
            const promise = new Promise(resolve => undoRes = resolve, reject => undoRej = reject)

            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('undo-apply-msg', { 't': tttimetable_name, 's': ttrecureventset_title }),
                cancelBtn: true,
                confirmCallBack: undoRes,
                cancelCallBack: undoRej,
            })

            await promise

            if (this.submitting) return
            this.submitting = true
            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/undo`, {
                method: 'DELETE',
                data: {
                    tttimetable_id,
                    ttrecureventset_id,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.$router.push(`/timetables/edit/${tttimetable_id}`)
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-undoing-apply')
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
        loadSet(recurSet) {
            if (!(recurSet.datefrom && recurSet.dateto && recurSet.grouptype_id)) return
            this.from = recurSet.datefrom
            this.until = recurSet.dateto
            this.selectType(this.groupTypes.find(gt => gt.id == recurSet.grouptype_id))
            this.selectedSet = recurSet
        },
        selectRecur(event) {
            this.showMenu = true

            const ids = event.uid.split('-')
            if (ids.length === 1) {
                this.$refs.menu.rotations = false
                this.$refs.menu.selectRecur(this.$refs.menu.recurEventsById[ids[0]])
            } else {
                const rotation = this.$refs.menu.rotationsById[ids[0]]
                delete rotation.ttsessiontype

                this.$refs.menu.rotations = true
                this.$refs.menu.selectRecur(rotation)
            }

            this.$refs.menu.$el.focus()
        },
        /**
         * Clears the event container of a recur event's entries and replaces with the events passed.
         * 
         * @param {number} recurId // We can pass -1 to clear the event container entirely.
         * @param {array} events 
         */
        applyRecur(recurId, events) {
            this.$refs.timetable.eventContainer.removeWhere(
                () => true, 
                () => true, 
                (event) => recurId == -1 || event.uid == recurId
            )
            this.events = [...this.events.filter(e => recurId != -1 && e.uid != recurId), ...events]
        },
        addSet(newSet) {
            this.recurEventSets.push(newSet)
            this.selectedSet = newSet
        },
        snapDate(isodate, forward=true, to=0) {
            let date = dateFromISODate(isodate)
            while (date.getDay() !== to) date.setDate(date.getDate() + (forward ? 1 : -1))
            return dateToISODate(date)
        },
        snapFrom(isodate) {
            this.from = this.snapDate(isodate, false, 1)
        },
        snapUntil(isodate) {
            this.until = this.snapDate(isodate, true, 0)
        },
        snapApplyFrom(isodate) {
            if (!this.selectedTimetable) return this.snapFrom(isodate)
            
            this.applyFrom = this.snapDate(isodate, false, 1)
            if (this.applyFrom < this.selectedTimetable.datefrom) this.applyFrom = this.snapDate(isodate, true, 1)
        },
        openMenu() {
            this.showMenu = true
            this.$refs.menu.resetSelected()
        },
        removeSet(id) {
            if (this.selectedSet && this.selectedSet.id == id) this.selectedSet = null
            this.recurEventSets = this.recurEventSets.filter(s => s.id != id)
        },
    },
    computed: {
        menuIsSubmitting() { 
            return this.$refs.menu ? this.$refs.menu.submitting : false
        }
    }
}
</script>